// THE PERSISTED ROW — one `task_run_events` record as the mothership stores it,
// append-only and hash-chained per run.
// Split out of `../task-run-event.ts`, which is now the barrel over this directory.

import { z } from 'zod';
import { TaskIdSchema } from '../ids';
import { TaskRunEventKindSchema } from './kind';

/**
 * Domain representation of one row in `task_run_events`.
 *
 * **Nothing parses a persisted row through this schema.** It documents the row's
 * shape (and its test pins it); it is not a read gate. `normalizeTaskRunEvent` in
 * `@developerz/db`, which this docblock claimed did that parse, has never existed —
 * `@developerz/db` serves these rows as the raw drizzle `$inferSelect` type, `kind`
 * included, so a reader who trusts a sentence like the old one will delete a
 * consumer's own validation as redundant. Where the checks actually are:
 *
 * - **On the way IN, not out** — {@link FleetRunEventSchema} is the enforcing gate,
 *   run twice on the same message (the box's `publishEvent` encode and the sink's
 *   pull). It validates `kind` against the closed {@link TaskRunEventKindSchema} and
 *   that `payload` is an object, and nothing more: a row's per-kind payload shape is
 *   never re-checked between that gate and any read.
 * - **Per-kind payload, at each consumer** — a consumer that acts on a payload parses
 *   it with that kind's own schema where it reads it: the event-sink on the wire event
 *   before persist (`PlanAuthored` / `KbLearnings` / `CapabilitiesDetected` /
 *   `ReviewPosted` / `RunStage`), the dashboard on the projected row
 *   (`parseRunStageEvent`), and `listRunProvenanceForRun` (`@developerz/db`) on the
 *   persisted row, which THROWS because it is the first thing to look at that payload
 *   since the box wrote it and its output is a public PR body. Not universal, and the
 *   exceptions are the point: readers that only TINT a view pick fields by hand and
 *   tolerate anything (`runEventOutcome` / `isTerminalRunEvent` in `apps/api`,
 *   `findLatestPlatformReviewVerdict`'s jsonb `->>`), which is fail-soft on purpose.
 *   Either way `payload` is typed `unknown` here — narrowing it is the reader's job.
 *
 * A kind's payload contract belongs in a schema BESIDE it — the per-kind shapes below,
 * or a module of its own when the payload is a value object other packages import
 * (`run_provenance` → `RunProvenanceSchema` in `./run-provenance`). Adding a kind
 * without one leaves its consumers narrowing an `unknown` by hand.
 *
 * `prevHash` is null on the first event of each run. The hash-chain scope is
 * `run_id` — NOT `task_id` — so a retried task begins a fresh chain.
 */
/**
 * The `status` payload's `event` value for the runner's liveness beat
 * (`apps/runner/src/guards.ts`). It carries `silent_ms` — how long since the run
 * last made REAL progress — and is emitted on a fixed cadence precisely BECAUSE
 * nothing else is happening.
 *
 * It lives in the domain because two seams must agree about it and cannot import
 * each other: the runner that emits it, and the stuck-task reaper's silence proof
 * in `packages/db` that must NOT count it as progress.
 *
 * WHY THAT MATTERS, measured. On 2026-08-23 a `fix_ci` babysit on
 * `tesote/tesote.ai` had been `in_progress` for THIRTY-EIGHT HOURS having emitted
 * 2,305 events, every single one of them this heartbeat — the last reporting
 * `silent_ms: 137765965`. The run was telling us, once a minute, that it had done
 * nothing since it started. The reaper's proof asked only whether an event had
 * ARRIVED, so a beat whose entire content is "I am idle" read as liveness, the
 * task was never reaped, and it held its repo's area gate with 87 tasks queued
 * behind it for two days.
 *
 * A liveness signal that reports silence must never be counted as activity.
 */
export const RUN_HEARTBEAT_EVENT = 'run_heartbeat' as const;

export const TaskRunEventSchema = z.object({
  /** UUID primary key (DB-minted). */
  id: z.string().uuid(),
  /** Execution run id — the `fleet.evt.<run_id>` stream key and chain scope. */
  runId: z.string().min(1),
  /** The owning task; cascade-deleted when the task is hard-deleted. */
  taskId: TaskIdSchema,
  ts: z.date(),
  kind: TaskRunEventKindSchema,
  /**
   * Event body (content + metadata). Null for content-free events such as a
   * bare status ping. Shape is `kind`-specific; consumers narrow on `kind`
   * before reading `payload`.
   */
  payload: z.unknown().nullable(),
  /** sha256 hex of the previous row's canonical JSON in this run; null on the first. */
  prevHash: z.string().nullable(),
});
export type TaskRunEvent = z.infer<typeof TaskRunEventSchema>;
